import React, { useRef } from 'react'
import { statusPanel } from '../../../../constants/copyright'
import Icon from '../icon/Icon'
import { useUser } from '../../../../redux/reducer/user'
import { useModal } from '../../../../hooks/useModal'
import classNames from 'classnames'
import { useTranslation } from '../../../../translations'
import { useApplicationCallbacks } from '../../../../providers/application-callbacks'
import { useFeatureToggle } from '../../../../hooks/useFeatureToggle'
import { BUTTONS, TEXT, TITLE } from '../../../modals/universalModal'
import useServiceData from '../../../../hooks/useServiceData'
import { gtmFunctions } from '../../../../constants/analytics/GTM'

const StatusPanel = React.memo(
  React.forwardRef(function ({ notClickable, type, mod, noChangeFontSize }, ref) {
    const { profile } = useUser()
    const { addModal } = useModal()
    const { t } = useTranslation()
    const callbacks = useApplicationCallbacks()
    const { get, save } = useServiceData()

    const gameTamagotchiBeauties = useFeatureToggle('gameTamagotchiBeauties')
    const gameTamagotchiEnableGiftConverter = useFeatureToggle('gameTamagotchiEnableGiftConverter')

    const openConverter = () => {
      addModal({ type: 'converterModal', props: { callbacks } })
    }

    const openConverterToCoinsModal = () => {
      if (gameTamagotchiEnableGiftConverter) {
        addModal({ type: 'converterToCoinsModal' })
      }
    }

    const activeClickedResource = useRef(null)

    const setActiveClickedResource = (value = null) => {
      activeClickedResource.current = value
    }

    const getUniversalModalProps = (index, buttonCallbacks) => {
      return {
        type: 'universalModal',
        props: {
          modalProps: { wrapper: { isFullPage: false }, modifier: 'noFullNoScroll' },
          components: [
            {
              list: [
                {
                  type: TITLE,
                  props: {
                    modifier: 'padding-no',
                    value: t(`statusPanel.icons.${index}.title`),
                  },
                },
                {
                  type: TEXT,
                  props: {
                    value: t(`statusPanel.icons.${index}.description`),
                  },
                },
                {
                  type: BUTTONS,
                  props: {
                    buttons: [
                      {
                        text: t('resourceContent.button.text'),
                        timeout: 1000,
                        customCallbacks: { setActiveClickedResource, openConverter },
                        callbackKey: ['setActiveClickedResource', 'openConverter'],
                        modalData: { action: 'close', data: { type: 'current' } },
                        ...buttonCallbacks,
                      },
                    ],
                  },
                },
              ],
            },
          ],
        },
      }
    }

    const clickOnResource = (id, name, i) => {
      if (notClickable || !!activeClickedResource.current) {
        return
      }

      const statusPanelItemsShowed = get('statusPanelItemsShowed') ?? {}

      setActiveClickedResource(name)

      if (!statusPanelItemsShowed[name]) {
        save('statusPanelItemsShowed', { ...statusPanelItemsShowed, [name]: true })
      }

      const callbacksLogic = {
        energy: () => {
          if (!statusPanelItemsShowed[name]) {
            addModal(
              getUniversalModalProps(i, {
                customCallbacks: { setActiveClickedResource, openConverter },
                callbackKey: ['setActiveClickedResource', 'openConverter'],
              }),
            )
          } else {
            setActiveClickedResource()

            openConverter()
          }
        },
        charm: () => {
          if (!statusPanelItemsShowed[name]) {
            addModal(
              getUniversalModalProps(i, {
                customCallbacks: { setActiveClickedResource, openConverterToCoinsModal },
                callbackKey: ['setActiveClickedResource', 'openConverterToCoinsModal'],
              }),
            )
          } else {
            setActiveClickedResource()

            openConverterToCoinsModal()
          }
        },
        other: () => {
          addModal(
            getUniversalModalProps(i, {
              customCallbacks: { setActiveClickedResource },
              callbackKey: 'setActiveClickedResource',
            }),
          )
        },
      }

      if (type === 'gift-choose') {
        callbacksLogic.other()

        return
      }

      if (name === 'energy') {
        gtmFunctions.GTMEnergy()
      }

      const callback = callbacksLogic[name] ?? callbacksLogic.other

      callback?.()
    }

    const statusPanelItems = (() => {
      const mainItems = ['coin', gameTamagotchiBeauties ? 'beauties' : 'gacha_event_cash']

      const customizationItems = ['coin', gameTamagotchiBeauties ? 'beauties' : 'gacha_event_cash']

      if (type === 'main') {
        return statusPanel.icons[type].filter((item) => mainItems.includes(item.name))
      }

      if (type === 'customization') {
        return statusPanel.icons[type].filter((item) => customizationItems.includes(item.name))
      }

      return statusPanel.icons[type]
    })()

    return (
      <div
        className={classNames('status-panel', {
          [`status-panel_${mod}`]: mod,
          [`status-panel_${type}`]: type,
        })}
        ref={ref}
      >
        {statusPanelItems.reduce((acc, item, id) => {
          const { i, name, icon, clickableIcon } = item

          const isGiftChooseSecondaryCurrency = type === 'gift-choose' && name === 'charm'
          const isGiftChooseBeauties = isGiftChooseSecondaryCurrency && gameTamagotchiBeauties
          const isGiftChooseGifts = isGiftChooseSecondaryCurrency && !gameTamagotchiBeauties
          const resolvedName = isGiftChooseBeauties ? 'beauties' : name
          const resolvedIndex = isGiftChooseSecondaryCurrency
            ? isGiftChooseBeauties
              ? '2'
              : '3'
            : i
          const value = isGiftChooseSecondaryCurrency
            ? isGiftChooseBeauties
              ? profile.beauties
              : profile.gacha_event_cash
            : profile[statusPanel.namesLinks[resolvedName]]

          // не выводим значение если это не число или строка
          if (value === null || value === undefined) {
            return acc
          }

          const itemIcon = isGiftChooseSecondaryCurrency
            ? isGiftChooseBeauties
              ? 'main/beauties'
              : 'main/gifts-white'
            : getClickableIconCondition({
                name,
                notClickable,
                gameTamagotchiEnableGiftConverter,
              })
            ? clickableIcon
            : icon

          return [
            ...acc,
            <div
              className={classNames(`status-panel__item status-panel__item_${id + 1}`, {
                clicked: activeClickedResource.current === resolvedName,
                'status-panel__item_gift-choose-gifts': isGiftChooseGifts,
              })}
              key={`expendable-${id}`}
              onClick={() => clickOnResource(id, resolvedName, resolvedIndex)}
              data-tutor-id={`nav-${resolvedName}`}
            >
              <div
                className={classNames('status-panel__item-icon', {
                  blue: resolvedName === 'coin',
                })}
              >
                <Icon name={itemIcon} />
              </div>
              <div className={'status-panel__item-value'}>
                <span
                  style={{
                    fontSize: `${value.length > 3 && !noChangeFontSize ? 3 / value.length : 1}em`,
                  }}
                >
                  {value}
                </span>
              </div>
            </div>,
          ]
        }, [])}
      </div>
    )
  }),
)

function getClickableIconCondition({ name, notClickable, gameTamagotchiEnableGiftConverter }) {
  if (notClickable) {
    return false
  }

  if (name === 'energy') {
    return true
  }

  if (name === 'charm' && gameTamagotchiEnableGiftConverter) {
    return true
  }

  return false
}

export default StatusPanel
StatusPanel.propTypes = {}
